import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Question } from '../types/game';
// TODO: Re-enable when favorites API is implemented
// import SaveToFavorites from '../components/SaveToFavorites';
import { unlockTimelineEvent } from '../services/timelineService';
// gameService no longer needed - results submitted during game play
import { calculateFinalScore, PERFECT_GAME_BONUS } from '../utils/scoring';
import './score-breakdown.css';

interface ResultsPageProps {}

// Define the Answer type to match what's used in the game
interface Answer {
  questionId: string;
  userAnswer: string;
  isCorrect: boolean;
  pointsEarned?: number;
  resultText?: string;
}

// Extended Question type that includes timeline-specific properties
interface ExtendedQuestion extends Question {
  categories?: string[];
  timelineEvent?: string;
  explanation?: string;
}

// Original ResultsState from trivia game
interface TriviaResultsState {
  questions: ExtendedQuestion[];
  answers: Answer[];
  settings: {
    difficulty: string;
    category: string;
    questionsCount: number;
  };
}

// New ResultsState from walk the story feature
interface WalkTheStoryResultsState {
  source: 'walkthestory';
  conversationId: string;
  score: number;
  characterName: string;
}

// New ResultsState from scenario game feature
interface ScenarioResultsState {
  gameType: 'scenario';
  score: number;
  totalQuestions: number;
  correctAnswers: number;
  answers: Answer[];
  scenarios: any[];
}

// Combined type to handle all result types
type ResultsState = TriviaResultsState | WalkTheStoryResultsState | ScenarioResultsState;

const ResultsPage: React.FC<ResultsPageProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth0();
  const results = location.state as ResultsState;
  
  const [finalScore, setFinalScore] = useState<number>(0);
  const [totalPossiblePoints, setTotalPossiblePoints] = useState<number>(0);
  const [isWalkTheStory, setIsWalkTheStory] = useState<boolean>(false);
  const [isScenarioGame, setIsScenarioGame] = useState<boolean>(false);
  // Session tracking no longer needed - results submitted during game play

  // Process results when component mounts
  useEffect(() => {
    if (!results) {
      return;
    }

    // Check if the results are from the Scenario game feature
    if ('gameType' in results && results.gameType === 'scenario') {
      setIsScenarioGame(true);
      setFinalScore(results.score);
      // For scenarios, calculate total possible points (each scenario can have different point values)
      const maxPossiblePoints = results.answers.reduce((total, answer) => total + (answer.pointsEarned || 0), 0);
      setTotalPossiblePoints(maxPossiblePoints);
      return;
    }

    // Check if the results are from the Walk the Story feature
    if ('source' in results && results.source === 'walkthestory') {
      setIsWalkTheStory(true);
      setFinalScore(results.score);
      // For Walk the Story, we don't have a total possible points, so set it to match score for 100%
      setTotalPossiblePoints(results.score);
      return;
    }

    // Handle trivia game results
    // Typescript will narrow the type to TriviaResultsState here
    if ('questions' in results && 'answers' in results) {
      // Get the base score from the game
      const baseScore = location.state?.score || results.answers.reduce(
        (total, answer) => total + (answer.pointsEarned || 0),
        0
      );
      
      // Calculate perfect game bonus if applicable
      const correctAnswers = results.answers.filter(a => a.isCorrect).length;
      const { finalScore: totalScore, perfectGameBonus } = calculateFinalScore(
        baseScore,
        results.questions.length,
        correctAnswers
      );
      
      // Calculate maximum possible points (varies by difficulty)
      const difficulty = location.state?.difficulty || 'medium';
      const maxStreak = location.state?.maxStreak || 0;
      
      // This is a rough estimate of max possible points
      // Actual max depends on speed and streak bonuses achieved
      let basePointsPerQuestion = 20; // default medium
      if (difficulty === 'easy') basePointsPerQuestion = 10;
      if (difficulty === 'hard') basePointsPerQuestion = 30;
      
      const maxPossiblePoints = (basePointsPerQuestion * 15) + // Base points
                               PERFECT_GAME_BONUS + // Perfect game bonus
                               150 + // Max streak bonus
                               (5 * 15); // Max speed bonuses
      
      setFinalScore(totalScore);
      setTotalPossiblePoints(maxPossiblePoints);
      
      // Store perfect game bonus for display
      if (perfectGameBonus > 0) {
        sessionStorage.setItem('perfectGameBonus', perfectGameBonus.toString());
      }
      sessionStorage.setItem('maxStreak', maxStreak.toString());
      
      // Check if any timeline events should be unlocked
      unlockTimelineEvents(results);
      
      // Game results already submitted during game play
    }
  }, [results]);
  
  // Session completion not needed - results are submitted during game play
  
  // Function to unlock timeline events based on correct answers to timeline questions
  const unlockTimelineEvents = async (triviaResults: TriviaResultsState) => {
    try {
      // Check if user is authenticated
      if (!isAuthenticated || !user) return; // Cannot unlock events if not logged in
      
      // Find all correct answers to timeline questions
      for (let i = 0; i < triviaResults.answers.length; i++) {
        const answer = triviaResults.answers[i];
        const question = triviaResults.questions[i];
        
        if (
          answer.isCorrect && 
          question.categories && 
          question.categories.includes('timeline') && 
          question.timelineEvent
        ) {
          // Unlock the timeline event associated with this question
          await unlockTimelineEvent(question.timelineEvent);
        }
      }
      
      // No need to update UI as the timeline page will get the latest unlocked events when visited
      
    } catch (error) {
      console.error('Error unlocking timeline events:', error);
      // Continue silently, don't interrupt the results page experience
    }
  };

  if (!results) {
    return (
      <div className="error-container">
        <h2>No results found</h2>
        <p>Please start a new game from the homepage</p>
        <button
          className="btn btn-primary mt-4"
          onClick={() => navigate('/')}
        >
          Go to Homepage
        </button>
      </div>
    );
  }

  // Calculate percentage score based on correct answers, not max possible points
  const correctAnswersCount = location.state?.answers?.filter((a: any) => a.isCorrect).length || 0;
  const totalQuestionsCount = location.state?.totalQuestions || location.state?.questions?.length || 15;
  const percentageScore = Math.round((correctAnswersCount / totalQuestionsCount) * 100) || 0;
  
  // Determine the message based on the percentage score
  let message = '';
  if (percentageScore >= 90) {
    message = 'Outstanding! Your Biblical knowledge is excellent!';
  } else if (percentageScore >= 75) {
    message = 'Great job! You really know your Scripture!';
  } else if (percentageScore >= 60) {
    message = 'Good effort! Keep studying the Word!';
  } else if (percentageScore >= 40) {
    message = 'Not bad! There\'s always more to learn in God\'s Word.';
  } else {
    message = 'Keep studying! God\'s Word has so much to teach us.';
  }

  // Render results for Scenario game feature
  if (isScenarioGame && 'gameType' in results && results.gameType === 'scenario') {
    const accuracy = results.totalQuestions > 0 ? Math.round((results.correctAnswers / results.totalQuestions) * 100) : 0;

    return (
      <div className="results-container">
        <h1 className="results-title">🎭 Biblical Scenarios Complete!</h1>

        <div className="results-summary-card">
          <div className="results-score">
            <div className="score-fraction">
              {finalScore} points
            </div>
            <div className="score-percentage">
              <div className="percentage-circle">
                <div className="percentage-number">{accuracy}%</div>
              </div>
            </div>
          </div>

          <div className="message-emoji" style={{textAlign: 'center', fontSize: '2.5rem', margin: '1rem auto'}}>
            📜
          </div>

          <div className="results-message">
            You've made {results.correctAnswers} wise decisions out of {results.totalQuestions} biblical scenarios!
          </div>

          <div className="scenario-stats" style={{marginTop: '1rem', textAlign: 'center'}}>
            <div style={{display: 'flex', justifyContent: 'center', gap: '2rem', marginBottom: '1rem'}}>
              <div>
                <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#10B981'}}>
                  {results.correctAnswers}
                </div>
                <div style={{color: '#666', fontSize: '0.9rem'}}>Recommended Choices</div>
              </div>
              <div>
                <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#F59E0B'}}>
                  {finalScore}
                </div>
                <div style={{color: '#666', fontSize: '0.9rem'}}>Total Points</div>
              </div>
            </div>
          </div>

          <div className="results-divider"></div>
        </div>

        <div className="results-actions">
          <button
            className="btn btn-primary"
            onClick={() => navigate('/')}
          >
            Return Home
          </button>

          <button
            className="btn btn-secondary"
            onClick={() => navigate('/scenario-game')}
          >
            Play Again
          </button>
        </div>
      </div>
    );
  }

  // Render results for Walk the Story feature
  if (isWalkTheStory && 'characterName' in results) {
    return (
      <div className="results-container">
        <h1 className="results-title">Conversation Results</h1>

        <div className="results-summary-card">
          <div className="results-score">
            <div className="score-fraction">
              {finalScore} points
            </div>
            <div className="score-percentage">
              <div className="percentage-circle">
                <div className="percentage-number">100%</div>
              </div>
            </div>
          </div>

          <div className="message-emoji" style={{textAlign: 'center', fontSize: '2.5rem', margin: '1rem auto'}}>
            ✨
          </div>

          <div className="results-message">
            You've completed your conversation with {results.characterName}!
          </div>

          <div className="results-divider"></div>
        </div>

        <div className="results-actions">
          <button
            className="btn btn-primary"
            onClick={() => navigate('/')}
          >
            Return Home
          </button>

          <button
            className="btn btn-secondary"
            onClick={() => navigate('/walk-the-story')}
          >
            More Conversations
          </button>
        </div>
      </div>
    );
  }

  // Render results for Trivia Game
  // TypeScript should narrow the type to TriviaResultsState here
  if ('questions' in results && 'answers' in results) {
    const { questions, answers, settings } = results;
    const correctAnswers = answers.filter(answer => answer.isCorrect).length;
    const totalQuestions = questions.length;
    
    // Ensure settings object exists to prevent "Cannot read properties of undefined" error
    const safeSettings = settings || { difficulty: 'N/A', category: 'N/A', questionsCount: 0 };

    return (
      <div className="results-container">
        <h1 className="results-title">Game Results</h1>
        
        <div className="results-summary-card">
          <div className="results-score">
            <div className="score-fraction">
              {finalScore} points
            </div>
            <div className="score-percentage">
              <div className="percentage-circle">
                <div className="percentage-number">{percentageScore}%</div>
              </div>
            </div>
          </div>
          
          {percentageScore >= 80 && (
            <div className="message-emoji" style={{textAlign: 'center', fontSize: '2.5rem', margin: '1rem auto'}}>
              👑
            </div>
          )}
          
          <div className="results-message">
            {message}
          </div>
          
          <div className="results-divider"></div>
          
          {/* Score Breakdown */}
          <div className="score-breakdown">
            <h3>Score Breakdown</h3>
            <div className="breakdown-items">
              <div className="breakdown-item">
                <span>Correct Answers:</span>
                <span>{correctAnswers} / {totalQuestions}</span>
              </div>
              {location.state?.maxStreak > 0 && (
                <div className="breakdown-item">
                  <span>Best Streak:</span>
                  <span>{location.state.maxStreak} in a row!</span>
                </div>
              )}
              {sessionStorage.getItem('perfectGameBonus') && parseInt(sessionStorage.getItem('perfectGameBonus')!) > 0 && (
                <div className="breakdown-item bonus">
                  <span>Perfect Game Bonus:</span>
                  <span>+{sessionStorage.getItem('perfectGameBonus')} pts 🎉</span>
                </div>
              )}
              <div className="breakdown-item total">
                <span>Total Score:</span>
                <span>{finalScore} points</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="question-review-container">
          {/* Performance Review Header */}
          <div className="hero-section">
            <div className="hero-content">
              <div className="hero-badge">
                <svg viewBox="0 0 24 24" fill="currentColor" style={{width: '20px', height: '20px'}}>
                  <path d="M3,3H21V21H3V3M13.5,12L16,14.5L22.5,8L21,6.5L16,11.5L14.5,10L13.5,12M6.5,10L8,11.5L6.5,13L4,10.5L6.5,10Z"/>
                </svg>
                <span>Question Analysis</span>
              </div>
              <h1 className="hero-title">Performance Review</h1>
              <p className="hero-description">
                Analyze your answers and deepen your biblical understanding
              </p>
              <div className="hero-stats">
                <div className="stat-item">
                  <div className="stat-value">{correctAnswers}</div>
                  <div className="stat-label">Correct</div>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <div className="stat-value">{totalQuestions - correctAnswers}</div>
                  <div className="stat-label">To Learn</div>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <div className="stat-value">{Math.round((correctAnswers / totalQuestions) * 100)}%</div>
                  <div className="stat-label">Accuracy</div>
                </div>
              </div>
            </div>
          </div>

          {/* Questions Container */}
          <div className="questions-masonry">
            {questions.map((question, index) => {
              const answer = answers[index];
              // Handle the correct answer text based on different option formats
              let correctOptionText = question.correctAnswer;
              if (Array.isArray(question.options)) {
                // Find the correct option object in the array
                const correctOption = question.options.find(opt => 
                  typeof opt === 'object' && 'isCorrect' in opt && opt.isCorrect === true
                );
                
                // If we found a correct option object with a text property, use it
                if (correctOption && typeof correctOption === 'object' && 'text' in correctOption) {
                  correctOptionText = correctOption.text;
                }
              }
              
              return (
                <div 
                  key={index} 
                  className={`question-tile ${answer.isCorrect ? 'tile-correct' : 'tile-incorrect'}`}
                >
                  {/* Tile Header */}
                  <div className="tile-header">
                    <div className="tile-number">
                      <div className="number-circle">
                        <span>{index + 1}</span>
                      </div>
                    </div>
                    <div className={`tile-status ${answer.isCorrect ? 'status-success' : 'status-error'}`}>
                      <div className="status-indicator">
                        {answer.isCorrect ? (
                          <svg viewBox="0 0 20 20" fill="currentColor" style={{width: '16px', height: '16px'}}>
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 20 20" fill="currentColor" style={{width: '16px', height: '16px'}}>
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                          </svg>
                        )}
                      </div>
                      <span className="status-label">
                        {answer.isCorrect ? 'Correct' : 'Incorrect'}
                      </span>
                    </div>
                    <div className="tile-points">
                      <div className="points-container">
                        <span className="points-value">
                          {answer.isCorrect ? `${answer.pointsEarned || question.points}` : '0'}
                        </span>
                        <span className="points-unit">pts</span>
                      </div>
                    </div>
                  </div>

                  {/* Question Content */}
                  <div className="tile-content">
                    <div className="question-block">
                      <h3 className="question-text">{question.text}</h3>
                    </div>

                    {/* Answer Comparison */}
                    <div className="answer-comparison">
                      <div className="answer-block user-answer">
                        <div className="answer-meta">
                          <div className="answer-avatar">
                            <svg viewBox="0 0 20 20" fill="currentColor" style={{width: '20px', height: '20px'}}>
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                            </svg>
                          </div>
                          <span className="answer-title">Your Answer</span>
                        </div>
                        <div className={`answer-value ${answer.isCorrect ? 'value-correct' : 'value-incorrect'}`}>
                          {answer.userAnswer}
                        </div>
                      </div>

                      {!answer.isCorrect && (
                        <div className="answer-block correct-answer">
                          <div className="answer-meta">
                            <div className="answer-avatar correct">
                              <svg viewBox="0 0 20 20" fill="currentColor" style={{width: '20px', height: '20px'}}>
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                              </svg>
                            </div>
                            <span className="answer-title">Correct Answer</span>
                          </div>
                          <div className="answer-value value-correct">
                            {correctOptionText || 'N/A'}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Explanation */}
                    {question.explanation && (
                      <div className="explanation-block">
                        <div className="explanation-meta">
                          <div className="explanation-icon">
                            <svg viewBox="0 0 20 20" fill="currentColor" style={{width: '20px', height: '20px'}}>
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                            </svg>
                          </div>
                          <span className="explanation-title">Explanation</span>
                        </div>
                        <p className="explanation-text">{question.explanation}</p>
                      </div>
                    )}

                    {/* Footer Tags */}
                    {(question.bibleReference || (question.type === 'scenario' && answer.resultText)) && (
                      <div className="tile-footer">
                        {question.bibleReference && (
                          <div className="reference-tag">
                            <svg className="tag-icon" viewBox="0 0 20 20" fill="currentColor" style={{width: '16px', height: '16px'}}>
                              <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z"/>
                            </svg>
                            <span className="tag-text">
                              {typeof question.bibleReference === 'string' 
                                ? question.bibleReference
                                : `${question.bibleReference.book} ${question.bibleReference.chapter}:${question.bibleReference.verses}`
                              }
                            </span>
                          </div>
                        )}
                        
                        {question.type === 'scenario' && answer.resultText && (
                          <div className="scenario-tag">
                            <svg className="tag-icon" viewBox="0 0 20 20" fill="currentColor" style={{width: '16px', height: '16px'}}>
                              <path fillRule="evenodd" d="M18 3a1 1 0 00-1.447-.894L8.763 6H5a3 3 0 000 6h.28l1.771 5.316A1 1 0 008 18h1a1 1 0 001-1v-4.382l6.553 3.276A1 1 0 0018 15V3z" clipRule="evenodd"/>
                            </svg>
                            <span className="tag-text">{answer.resultText}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="results-actions">
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/')}
          >
            Return Home
          </button>
          
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/play', { 
              state: { 
                settings: safeSettings 
              } 
            })}
          >
            Play Again
          </button>
        </div>
      </div>
    );
  }

  // Fallback in case the results don't match either format
  return (
    <div className="error-container">
      <h2>Unexpected results format</h2>
      <p>We encountered an error processing your results.</p>
      <button
        className="btn btn-primary mt-4"
        onClick={() => navigate('/')}
      >
        Go to Homepage
      </button>
    </div>
  );
};

export default ResultsPage;